import { Injectable, inject } from '@angular/core';
import { User } from '@auth0/auth0-angular';
import { datadogRum } from '@datadog/browser-rum';
import { FeatureFlagService } from 'common';
import { Observable, switchMap, tap } from 'rxjs';
import { RbacPermissionsService } from 'rbac/lib/casl-permissions/rbac-permissions.service';
import { NAMESPACE } from 'rbac/lib/constants/namespace';

@Injectable({ providedIn: 'root' })
export class ShowTixAuthService {
  private userId$ = inject<Observable<string>>('USER_ID' as any);

  private readonly rbacPermissionsService = inject(RbacPermissionsService);
  private readonly featureFlags = inject(FeatureFlagService);

  /**
   * Checks if the user is authenticated, and if not, redirects them to login
   * @returns if the user is authenticated and has the correct roles
   */
  public checkAuth(): Observable<User | null | undefined> {
    return this.userId$.pipe(
      switchMap((userId) => this.rbacPermissionsService.getUser$(userId)),
      tap((user) => {
        if (!user || !this.hasShowTixAccess(user)) {
          datadogRum.clearUser();
          this.featureFlags.setUser(null);
        } else {
          datadogRum.setUser({
            id: user[`${NAMESPACE}/employeeNumber`],
            name: user.name,
            email: user.email,
          });

          this.featureFlags.setUser({
            id: user.sub!,
            email: user.email!,
            custom: {
              employeeNumber: user[`${NAMESPACE}/employeeNumber`],
            },
          });
        }
      })
    );
  }

  private hasShowTixAccess(user: User) {
    const roles = user[`${NAMESPACE}/roles`] as string[];

    const isDeveloper = roles.includes('DEVELOPER');

    if (isDeveloper) {
      // developers bypass the role check
      return true;
    }

    const showTixRoles = roles.filter((role) => role.startsWith('SHOWTIX_'));
    return showTixRoles.length > 0;
  }
}
